import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Col,
  Form,
  InputNumber,
  Row,
  Select,
  Skeleton,
  Tooltip,
  message,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import DashboardPageWrapper from '../../../../components/layout/DashboardPageWrapper';
import basketService from '../../../../services/basketService';
import basketOrderService from '../../../../services/basketOrderService';
import ifaService from '../../../../services/ifaService';
import useRemoteSelectPage from '../../../../hooks/useRemoteSelectPage';
import { getPublicId } from './basketOrder.helpers';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getPartnerUuid(p) { return p?.id ?? p?.uuid ?? ''; }
function getInvestorUuid(inv) { return inv?.id ?? inv?.uuid ?? ''; }
function getBasketKey(basket) { return getPublicId(basket) || basket?.id || basket?.uuid || ''; }

const INR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
});
function fmtInr(v) {
  const n = Number(v);
  return Number.isFinite(n) ? INR.format(n) : '—';
}

function extractBasketsFromListResponse(res) {
  const payload = res?.success && res?.data ? res.data : res?.data ?? res;
  const raw = payload?.baskets ?? [];
  return (Array.isArray(raw) ? raw : []).map((entry) => entry?.basket ?? entry);
}

function initials(str) { return (str || '?').trim().substring(0, 2).toUpperCase(); }

// ─── UI Primitives (match PlaceSingleShareOrderPage) ──────────────────────────

const LABEL_CLS =
  '[&_.ant-form-item-label>label]:text-xs [&_.ant-form-item-label>label]:font-semibold [&_.ant-form-item-label>label]:uppercase [&_.ant-form-item-label>label]:tracking-wide [&_.ant-form-item-label>label]:text-gray-500 [&_.ant-form-item-label]:pb-1.5';

const ENTITY_SELECT_CLS =
  'w-full order-entity-select [&_.ant-select-selector]:!min-h-[76px] [&_.ant-select-selector]:!h-auto [&_.ant-select-selector]:!items-center [&_.ant-select-selector]:!py-2.5 [&_.ant-select-selector]:!px-3 [&_.ant-select-selection-item]:!h-auto [&_.ant-select-selection-item]:!max-h-none [&_.ant-select-selection-item]:!overflow-visible [&_.ant-select-selection-item]:!whitespace-normal [&_.ant-select-selection-item]:!leading-normal [&_.ant-select-selection-item]:!py-0';

const ENTITY_OPTION_PAD = 'py-2.5';
const SELECT_PAGE_SIZE = 50;

function extractPaginationFromListResponse(res) {
  const payload = res?.success && res?.data ? res.data : res?.data ?? res;
  return payload?.pagination;
}

function EntityAvatar({ label, variant = 'primary', rounded = 'full' }) {
  const sz = 'h-9 w-9 shrink-0 text-xs font-semibold';
  const round = rounded === 'md' ? 'rounded-md' : 'rounded-full';
  const tones = {
    primary: 'bg-primary-muted text-primary',
    indigo: 'bg-indigo-100 text-indigo-700',
    slate: 'bg-slate-100 text-slate-600',
  };
  return (
    <span
      className={`${sz} ${round} flex items-center justify-center ${tones[variant] ?? tones.primary}`}
    >
      {initials(label)}
    </span>
  );
}

function StackedEntityOption({ title, line2, line3, variant = 'primary', imageRounded = 'full' }) {
  if (!title) return null;
  return (
    <div className={`flex items-start gap-3 ${ENTITY_OPTION_PAD}`}>
      <EntityAvatar label={title} variant={variant} rounded={imageRounded} />
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="truncate text-sm font-medium leading-tight text-gray-900">{title}</p>
        {line2 ? (
          <p className="mt-1 truncate text-xs leading-tight text-gray-600">{line2}</p>
        ) : null}
        {line3 ? (
          <p className="mt-0.5 truncate text-xs leading-tight text-gray-400">{line3}</p>
        ) : null}
      </div>
    </div>
  );
}

function buildPartnerMeta(partner) {
  if (!partner) return null;
  const title = partner.name || partner.email || '—';
  return {
    title,
    line2: partner.pan || partner.email || partner.display_id || null,
    line3: partner.mobile || null,
    searchLabel: [title, partner.email, partner.display_id, partner.pan, partner.mobile]
      .filter(Boolean)
      .join(' '),
  };
}

function buildInvestorMeta(investor) {
  if (!investor) return null;
  const title = investor.name || '—';
  return {
    title,
    line2: investor.pan || investor.email || null,
    line3: investor.mobile || null,
    searchLabel: [title, investor.pan, investor.email, investor.mobile, investor.folio_id]
      .filter(Boolean)
      .join(' '),
  };
}

function buildBasketMeta(basket) {
  if (!basket) return null;
  const title = basket.name || basket.slug || getPublicId(basket) || '—';
  const minPrice = basket.min_price != null ? `Min ${fmtInr(basket.min_price)}` : null;
  return {
    title,
    line2: minPrice,
    line3: null,
    searchLabel: [title, basket.slug].filter(Boolean).join(' '),
  };
}

// ─── Summary card ─────────────────────────────────────────────────────────────

function BasketOrderSummary({ result, canPlace, placing, onPlace }) {
  if (!result) return null;

  const {
    total_basket_price,
    commission_margin_amount,
    items = [],
  } = result;

  return (
    <div className="mt-5 rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200">
        <p className="text-sm font-semibold text-gray-900">Summary</p>
      </div>

      {/* Highlight rows */}
      <div className="divide-y divide-gray-100">
        <div className="flex items-center justify-between px-4 py-3 bg-[#f0f7ee]">
          <span className="text-sm text-gray-600">Total basket price</span>
          <span className="text-sm font-semibold text-gray-900 tabular-nums">
            {fmtInr(total_basket_price)}
          </span>
        </div>
        <div className="flex items-center justify-between px-4 py-3 bg-[#f0f7ee]">
          <span className="text-sm text-gray-600">Your commission on this deal</span>
          <span className="text-sm font-semibold text-gray-900 tabular-nums">
            {fmtInr(commission_margin_amount)}
          </span>
        </div>
      </div>

      {/* Basket breakdown */}
      {items.length > 0 ? (
        <div className="px-4 pt-4 pb-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Basket breakdown
          </p>
          <div className="divide-y divide-gray-100">
            {items.map((item, idx) => (
              <div
                key={item.product_id ?? idx}
                className="flex items-start justify-between py-3"
              >
                <div className="min-w-0 flex-1 pr-4">
                  <p className="text-sm font-semibold text-gray-900 leading-tight">
                    {item.product_name}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {item.percentage}% &middot; Qty: {item.quantity}
                  </p>
                </div>
                <span className="text-sm font-medium text-gray-900 tabular-nums shrink-0">
                  {fmtInr(item.item_investment)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Place order button */}
      <div className="px-4 pb-4 pt-2">
        <Tooltip title={canPlace ? undefined : 'Fill all required fields to place order'}>
          <Button
            type="primary"
            size="large"
            block
            disabled={!canPlace || placing}
            loading={placing}
            onClick={onPlace}
            className="h-12! rounded-xl! text-base! font-semibold!"
          >
            Place order
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function PlaceBasketOrderPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const partnerUuidFromUrl = searchParams.get('partner_uuid')?.trim() || '';
  const investorIdFromUrl = searchParams.get('investor_id')?.trim() || '';
  const basketIdFromUrl = searchParams.get('basket_id')?.trim() || '';

  const [form] = Form.useForm();

  const selectedPartnerUuid = Form.useWatch('partner_uuid', form);
  const selectedInvestorId = Form.useWatch('investor_id', form);
  const selectedBasketId = Form.useWatch('basket_id', form);
  const investmentAmount = Form.useWatch('investment_amount', form);
  const commissionPct = Form.useWatch('commission_percentage', form);

  const [loadError, setLoadError] = useState('');

  const [calculateResult, setCalculateResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const partnerPrefilledRef = useRef(false);
  const investorPrefilledRef = useRef(false);
  const basketPrefilledRef = useRef(false);
  const calcDebounceRef = useRef(null);

  // Self-buy: partner's own UUID === selected investor's UUID
  const isPartnerSelfBuy = useMemo(
    () =>
      Boolean(
        selectedPartnerUuid &&
          selectedInvestorId &&
          String(selectedPartnerUuid) === String(selectedInvestorId)
      ),
    [selectedPartnerUuid, selectedInvestorId]
  );

  const effectiveMargin = isPartnerSelfBuy ? 0 : (commissionPct ?? 0);

  const canPlaceOrder =
    Boolean(selectedPartnerUuid) &&
    Boolean(selectedInvestorId) &&
    Boolean(selectedBasketId) &&
    Boolean(investmentAmount) &&
    Number(investmentAmount) > 0 &&
    Boolean(calculateResult);

  // ── Data fetching ────────────────────────────────────────────────────────────

  const fetchPartnersPage = useCallback(async ({ page, limit, search, signal }) => {
    setLoadError('');
    const res = await ifaService.getAdminIfas({
      page,
      limit,
      search,
      status: 'active',
      signal,
    });
    return {
      items: Array.isArray(res?.users) ? res.users : [],
      pagination: res?.pagination,
    };
  }, []);

  const fetchBasketsPage = useCallback(async ({ page, limit, search, signal }) => {
    const res = await basketService.listBaskets({
      page,
      limit,
      search,
      status: 'ACTIVE',
      signal,
    });
    return {
      items: extractBasketsFromListResponse(res),
      pagination: extractPaginationFromListResponse(res),
    };
  }, []);

  const fetchInvestorsPage = useCallback(
    async ({ page, limit, search, signal }) => {
      if (!selectedPartnerUuid) return { items: [], pagination: null };
      const res = await ifaService.getPartnerProfileSection(selectedPartnerUuid, 'investors', {
        page,
        limit,
        search,
        signal,
      });
      const list = Array.isArray(res?.data) ? res.data : [];
      return {
        items: list.map((inv) => ({
          id: inv.uuid,
          name: inv.name,
          email: inv.email,
          mobile: inv.mobile,
          pan: inv.pan,
          folio_id: inv.folio_id,
        })),
        pagination: res?.pagination,
      };
    },
    [selectedPartnerUuid]
  );

  const {
    items: partners,
    loading: loadingPartners,
    handleSearch: handlePartnerSearch,
    handlePopupScroll: handlePartnerPopupScroll,
    handleOpenChange: handlePartnerOpenChange,
    dropdownRender: partnerDropdownRender,
    searchValue: partnerSearchValue,
  } = useRemoteSelectPage({
    fetchPage: fetchPartnersPage,
    getKey: getPartnerUuid,
    pageSize: SELECT_PAGE_SIZE,
    selectedValue: selectedPartnerUuid,
    prefetchOnMount: Boolean(partnerUuidFromUrl),
    prefetchSearch: '',
    onError: (e) => setLoadError(e?.response?.data?.message || e?.message || 'Failed to load partners'),
  });

  const {
    items: investors,
    loading: loadingInvestors,
    handleSearch: handleInvestorSearch,
    handlePopupScroll: handleInvestorPopupScroll,
    handleOpenChange: handleInvestorOpenChange,
    dropdownRender: investorDropdownRender,
    searchValue: investorSearchValue,
  } = useRemoteSelectPage({
    enabled: Boolean(selectedPartnerUuid),
    fetchPage: fetchInvestorsPage,
    getKey: getInvestorUuid,
    pageSize: SELECT_PAGE_SIZE,
    selectedValue: selectedInvestorId,
    prefetchOnMount: Boolean(investorIdFromUrl),
    prefetchSearch: '',
    onError: (e) => message.error(e?.response?.data?.message || e?.message || 'Failed to load investors'),
  });

  const {
    items: baskets,
    loading: loadingBaskets,
    handleSearch: handleBasketSearch,
    handlePopupScroll: handleBasketPopupScroll,
    handleOpenChange: handleBasketOpenChange,
    dropdownRender: basketDropdownRender,
    searchValue: basketSearchValue,
  } = useRemoteSelectPage({
    fetchPage: fetchBasketsPage,
    getKey: getBasketKey,
    pageSize: SELECT_PAGE_SIZE,
    selectedValue: selectedBasketId,
    prefetchOnMount: Boolean(basketIdFromUrl),
    prefetchSearch: '',
    onError: (e) => message.error(e?.response?.data?.message || e?.message || 'Failed to load baskets'),
  });

  // ── Derived ─────────────────────────────────────────────────────────────────

  const selectedPartner = useMemo(
    () => partners.find((p) => getPartnerUuid(p) === selectedPartnerUuid) ?? null,
    [partners, selectedPartnerUuid]
  );
  const selectedInvestor = useMemo(
    () => investors.find((inv) => inv.id === selectedInvestorId) ?? null,
    [investors, selectedInvestorId]
  );
  const selectedBasket = useMemo(
    () => baskets.find((b) => getPublicId(b) === selectedBasketId) ?? null,
    [baskets, selectedBasketId]
  );

  // ── Prefill from URL ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (partnerPrefilledRef.current || !partnerUuidFromUrl || !partners.length) return;
    const m = partners.find((p) => getPartnerUuid(p) === partnerUuidFromUrl);
    if (!m) return;
    partnerPrefilledRef.current = true;
    form.setFieldValue('partner_uuid', getPartnerUuid(m));
  }, [partnerUuidFromUrl, partners, form]);

  useEffect(() => {
    if (investorPrefilledRef.current || !investorIdFromUrl || !selectedPartnerUuid || loadingInvestors) return;
    const m = investors.find((inv) => inv.id === investorIdFromUrl);
    if (!m) return;
    investorPrefilledRef.current = true;
    form.setFieldValue('investor_id', m.id);
  }, [investorIdFromUrl, investors, selectedPartnerUuid, loadingInvestors, form]);

  useEffect(() => {
    if (basketPrefilledRef.current || !basketIdFromUrl || !baskets.length) return;
    const m = baskets.find((b) => getPublicId(b) === basketIdFromUrl || b.slug === basketIdFromUrl);
    if (!m) return;
    basketPrefilledRef.current = true;
    form.setFieldValue('basket_id', getPublicId(m));
  }, [basketIdFromUrl, baskets, form]);

  // ── URL sync ─────────────────────────────────────────────────────────────────

  const syncToUrl = useCallback(
    (updates = {}) => {
      const pu =
        updates.partnerUuid !== undefined ? updates.partnerUuid : form.getFieldValue('partner_uuid');
      const ii =
        updates.investorId !== undefined ? updates.investorId : form.getFieldValue('investor_id');
      const bi =
        updates.basketId !== undefined ? updates.basketId : form.getFieldValue('basket_id');
      const p = new URLSearchParams();
      if (pu) p.set('partner_uuid', String(pu));
      if (ii) p.set('investor_id', String(ii));
      if (bi) p.set('basket_id', String(bi));
      setSearchParams(p, { replace: true });
    },
    [setSearchParams, form]
  );

  const handlePartnerChange = (v) => {
    investorPrefilledRef.current = true;
    form.setFieldsValue({ partner_uuid: v, investor_id: undefined });
    setCalculateResult(null);
    syncToUrl({ partnerUuid: v, investorId: undefined });
  };

  const handleInvestorChange = (v) => {
    setCalculateResult(null);
    syncToUrl({ investorId: v });
  };

  const handleBasketChange = (v) => {
    setCalculateResult(null);
    syncToUrl({ basketId: v });
  };

  // ── Live calculate (debounced) ────────────────────────────────────────────────

  const runCalculate = useCallback(
    async ({ basketId, partnerId, amount, margin }) => {
      if (!basketId || !partnerId || !amount || Number(amount) <= 0) return;
      setCalculating(true);
      try {
        const result = await basketOrderService.adminCalculatePrice(basketId, {
          partner_id: partnerId,
          amount,
          margin: margin ?? 0,
        });
        setCalculateResult(result);
      } catch (e) {
        setCalculateResult(null);
        message.error(e?.response?.data?.message || e?.message || 'Price calculation failed');
      } finally {
        setCalculating(false);
      }
    },
    []
  );

  useEffect(() => {
    const basketId = selectedBasketId;
    const partnerId = selectedPartnerUuid;
    const amount = investmentAmount;
    const margin = isPartnerSelfBuy ? 0 : (commissionPct ?? 0);

    if (!basketId || !partnerId || !amount || Number(amount) <= 0) {
      setCalculateResult(null);
      return;
    }

    if (calcDebounceRef.current) clearTimeout(calcDebounceRef.current);
    calcDebounceRef.current = setTimeout(() => {
      runCalculate({ basketId, partnerId, amount, margin });
    }, 600);

    return () => {
      if (calcDebounceRef.current) clearTimeout(calcDebounceRef.current);
    };
  }, [selectedBasketId, selectedPartnerUuid, investmentAmount, commissionPct, isPartnerSelfBuy, runCalculate]);

  // ── Place order ───────────────────────────────────────────────────────────────

  const handlePlace = async () => {
    if (!canPlaceOrder) return;
    try {
      await form.validateFields(['partner_uuid', 'investor_id', 'basket_id', 'investment_amount']);
    } catch {
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        basket_id: selectedBasketId,
        partner_id: selectedPartnerUuid,
        amount: Number(investmentAmount),
        margin: effectiveMargin,
      };
      if (!isPartnerSelfBuy && selectedInvestorId) {
        payload.investor_id = selectedInvestorId;
      }
      await basketOrderService.adminPlaceBasketOrder(payload);
      message.success('Basket order placed successfully');
      navigate('/pre-ipo/basket-order-management');
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Option lists ──────────────────────────────────────────────────────────────

  const partnerOptions = useMemo(
    () =>
      partners.map((p) => {
        const meta = buildPartnerMeta(p);
        return { value: getPartnerUuid(p), label: meta?.searchLabel || getPartnerUuid(p), partner: p };
      }),
    [partners]
  );

  const investorOptions = useMemo(
    () =>
      investors.map((inv) => {
        const meta = buildInvestorMeta(inv);
        return { value: inv.id, label: meta?.searchLabel || inv.id, investor: inv };
      }),
    [investors]
  );

  const basketOptions = useMemo(
    () =>
      baskets.map((b) => {
        const meta = buildBasketMeta(b);
        return { value: getPublicId(b), label: meta?.searchLabel || getPublicId(b), basket: b };
      }),
    [baskets]
  );

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <DashboardPageWrapper>
      <div className="mx-auto max-w-3xl">
        {loadError ? (
          <Alert type="error" showIcon message={loadError} className="mb-3" />
        ) : null}

        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          {/* Header */}
          <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-2.5 sm:px-5">
            <button
              type="button"
              onClick={() => navigate('/pre-ipo/basket-order-management')}
              aria-label="Go back"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
            >
              <ArrowLeftOutlined style={{ fontSize: 14 }} />
            </button>
            <h1 className="text-base font-semibold text-gray-900">Place basket order</h1>
          </div>

          <div className="p-4 sm:p-5">
            <Form form={form} layout="vertical" requiredMark={false} className={LABEL_CLS}>

              {/* ── Entity selectors ── */}
              <Row gutter={[12, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="partner_uuid"
                    label="Partner"
                    className="mb-3!"
                    rules={[{ required: true, message: 'Select a partner' }]}
                  >
                    <Select
                      showSearch
                      autoClearSearchValue={false}
                      allowClear={!partnerUuidFromUrl}
                      loading={loadingPartners}
                      placeholder="Select partner"
                      optionFilterProp="label"
                      filterOption={false}
                      searchValue={partnerSearchValue}
                      onSearch={handlePartnerSearch}
                      onPopupScroll={handlePartnerPopupScroll}
                      onOpenChange={handlePartnerOpenChange}
                      dropdownRender={partnerDropdownRender}
                      onChange={handlePartnerChange}
                      className={ENTITY_SELECT_CLS}
                      options={partnerOptions}
                      listHeight={360}
                      popupMatchSelectWidth
                      styles={{ popup: { root: { paddingBlock: 4 } } }}
                      optionRender={(opt) => {
                        const m = buildPartnerMeta(opt.data?.partner);
                        return m ? (
                          <StackedEntityOption
                            title={m.title}
                            line2={m.line2}
                            line3={m.line3}
                            variant="indigo"
                          />
                        ) : null;
                      }}
                      labelRender={() => {
                        const m = buildPartnerMeta(selectedPartner);
                        return m ? (
                          <StackedEntityOption
                            title={m.title}
                            line2={m.line2}
                            line3={m.line3}
                            variant="indigo"
                          />
                        ) : (
                          <span className="text-sm text-gray-400">Select partner</span>
                        );
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="investor_id"
                    label="Investor"
                    className="mb-3!"
                    rules={[{ required: true, message: 'Select an investor' }]}
                  >
                    <Select
                      showSearch
                      autoClearSearchValue={false}
                      allowClear
                      disabled={!selectedPartnerUuid}
                      loading={loadingInvestors}
                      placeholder={selectedPartnerUuid ? 'Select investor' : 'Select partner first'}
                      optionFilterProp="label"
                      filterOption={false}
                      searchValue={investorSearchValue}
                      onSearch={handleInvestorSearch}
                      onPopupScroll={handleInvestorPopupScroll}
                      onOpenChange={handleInvestorOpenChange}
                      dropdownRender={investorDropdownRender}
                      onChange={handleInvestorChange}
                      className={ENTITY_SELECT_CLS}
                      options={investorOptions}
                      listHeight={360}
                      popupMatchSelectWidth
                      styles={{ popup: { root: { paddingBlock: 4 } } }}
                      optionRender={(opt) => {
                        const m = buildInvestorMeta(opt.data?.investor);
                        return m ? (
                          <StackedEntityOption
                            title={m.title}
                            line2={m.line2}
                            line3={m.line3}
                            variant="primary"
                          />
                        ) : null;
                      }}
                      labelRender={() => {
                        const m = buildInvestorMeta(selectedInvestor);
                        return m ? (
                          <StackedEntityOption
                            title={m.title}
                            line2={m.line2}
                            line3={m.line3}
                            variant="primary"
                          />
                        ) : (
                          <span className="text-sm text-gray-400">
                            {selectedPartnerUuid ? 'Select investor' : 'Select partner first'}
                          </span>
                        );
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    name="basket_id"
                    label="Basket"
                    className="mb-0!"
                    rules={[{ required: true, message: 'Select a basket' }]}
                  >
                    <Select
                      showSearch
                      autoClearSearchValue={false}
                      allowClear
                      loading={loadingBaskets}
                      placeholder="Select basket"
                      optionFilterProp="label"
                      filterOption={false}
                      searchValue={basketSearchValue}
                      onSearch={handleBasketSearch}
                      onPopupScroll={handleBasketPopupScroll}
                      onOpenChange={handleBasketOpenChange}
                      dropdownRender={basketDropdownRender}
                      onChange={handleBasketChange}
                      className={ENTITY_SELECT_CLS}
                      options={basketOptions}
                      listHeight={360}
                      popupMatchSelectWidth
                      styles={{ popup: { root: { paddingBlock: 4 } } }}
                      optionRender={(opt) => {
                        const m = buildBasketMeta(opt.data?.basket);
                        return m ? (
                          <StackedEntityOption
                            title={m.title}
                            line2={m.line2}
                            line3={m.line3}
                            variant="slate"
                            imageRounded="md"
                          />
                        ) : null;
                      }}
                      labelRender={() => {
                        const m = buildBasketMeta(selectedBasket);
                        return m ? (
                          <StackedEntityOption
                            title={m.title}
                            line2={m.line2}
                            line3={m.line3}
                            variant="slate"
                            imageRounded="md"
                          />
                        ) : (
                          <span className="text-sm text-gray-400">Select basket</span>
                        );
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* ── Investment & margin ── */}
              {selectedBasketId && selectedPartnerUuid && selectedInvestorId ? (
                <div className="mt-5 border-t border-gray-100 pt-5">
                  {/* Self-buy badge */}
                  {isPartnerSelfBuy ? (
                    <div className="mb-4 flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3.5 py-2.5">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white text-[10px] font-bold">
                        P
                      </span>
                      <span className="text-sm text-indigo-800">
                        <strong>Partner self-buy</strong> — margin is not applicable
                      </span>
                    </div>
                  ) : null}

                  <Row gutter={[12, 0]}>
                    <Col xs={24} sm={isPartnerSelfBuy ? 24 : 12}>
                      <Form.Item
                        name="investment_amount"
                        label="Investment amount"
                        className="mb-0!"
                        rules={[{ required: true, message: 'Enter investment amount' }]}
                      >
                        <InputNumber
                          className="w-full"
                          min={1}
                          step={1000}
                          precision={0}
                          placeholder="e.g. 50000"
                          prefix={<span className="text-gray-400">₹</span>}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>

                    {!isPartnerSelfBuy ? (
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="commission_percentage"
                          label="Margin %"
                          className="mb-0!"
                          initialValue={0}
                        >
                          <InputNumber
                            className="w-full"
                            min={0}
                            max={100}
                            step={0.1}
                            precision={2}
                            placeholder="0.00"
                            addonAfter={<span className="text-gray-500 text-sm">%</span>}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                    ) : null}
                  </Row>

                  {/* Live summary */}
                  {calculating ? (
                    <div className="mt-5">
                      <Skeleton active paragraph={{ rows: 4 }} />
                    </div>
                  ) : (
                    <BasketOrderSummary
                      result={calculateResult}
                      canPlace={canPlaceOrder}
                      placing={submitting}
                      onPlace={handlePlace}
                    />
                  )}
                </div>
              ) : null}

            </Form>
          </div>
        </div>
      </div>
    </DashboardPageWrapper>
  );
}
